import { useState } from "react";
import { X } from "lucide-react";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import type {
  ListFilterChoice,
  Command,
  FieldError,
} from "@shared-core/domain/object-ui/contract";
import {
  emptyListQuery,
  type FilterValue,
} from "@shared-core/domain/object-ui/list-query";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import type { FieldDescriptor } from "@shared-core/domain/object-ui/widgets";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import { Label } from "../../primitives/label";
import { EntityCombobox, type EntityOption } from "../EntityCombobox";
import { fieldId, FieldFrame, invalidProps } from "./CommandFields";
import { optionOf } from "./entity-option";

function chosenOf(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];
}

/**
 * What the box shows once an option is picked: its label and, after it, the hint the list showed beneath it. A customer
 * is listed by its CIS code with its name as the hint, so the box reads "CUS-A00100 · Acme Print Ltd", not the code alone.
 */
function namedOption(option: EntityOption): EntityOption {
  return option.hint
    ? { id: option.id, label: `${option.label} · ${option.hint}` }
    : option;
}

/**
 * The entity picker (brief §9.3): searches the named object's own list and
 * sends its identifier, or for an array field (`multiple`) adds each pick to
 * the identifiers it sends. Where the dry run gives starting filters, the
 * picker opens on them ("Suggested only") and the person can switch them off; where it offers them as choices
 * (`list_filter_choices`), each is drawn above the box as values to add or remove (Catalogues), hidden once the field
 * holds a value.
 * Where the dry run fixes a scope (this order's customer's people), the
 * search never leaves it; a scope that names a list with nothing in it (no
 * customer chosen yet) has nobody to find, so the box is disabled. The field sends the list's key, or the column its
 * `entity_value` names. Where the dry run offers the object's create command
 * for this record, "Create…" runs it nested in the same shell and picks what it
 * created.
 */
export function EntityField({
  field,
  value,
  errors,
  disabled,
  create,
  listFilters = null,
  listFilterChoices = null,
  listScope = null,
  note = null,
  knownAs = null,
  valueLabel = null,
  valueLabels = null,
  onChange,
  onCreate,
}: {
  field: Extract<FieldDescriptor, { kind: "entity" | "suggest" }>;
  value: unknown;
  errors: FieldError[];
  disabled: boolean;
  /** The dry run's `field_hints[path].create`, when the person may create one here. */
  create: Command | null;
  /** The dry run's `field_hints[path].list_filters`: the list params the picker opens on. */
  listFilters?: Record<string, FilterValue> | null;
  /** The dry run's `field_hints[path].list_filter_choices`: starting filters the person changes value by value. */
  listFilterChoices?: ListFilterChoice[] | null;
  /** The dry run's `field_hints[path].list_scope`: list params always applied, never switched off. */
  listScope?: Record<string, FilterValue> | null;
  /** The dry run's `field_hints[path].note`: one sentence under the picker, worded by the backend. */
  note?: string | null;
  /** What the value the record already holds is called, when the page knows it (the setting's fact), so the box shows a name, not an id. */
  knownAs?: EntityOption | null;
  /** The dry run's `field_hints[path].value_label`: the words for the value the field starts on. */
  valueLabel?: string | null;
  /** The dry run's `field_hints[path].value_labels`: the words for each value a many-valued field holds, by value. */
  valueLabels?: Record<string, string> | null;
  /** `option` is what a single-value pick chose (or created), so the caller can keep its name. */
  onChange: (
    value: string | string[] | null,
    option?: EntityOption | null,
  ) => void;
  /** Run the create command nested; resolves with the created id, or null. */
  onCreate: (command: Command) => Promise<string | null>;
}) {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const choices = listFilterChoices ?? [];
  const hasSuggestions =
    choices.length === 0 &&
    listFilters !== null &&
    Object.keys(listFilters).length > 0;
  const [isSuggestedOnly, setSuggestedOnly] = useState(true);
  // Each filter offered as a choice starts on the dry run's values; the person adds and removes values from there.
  const [chosenFilters, setChosenFilters] = useState<Record<string, string[]>>(
    {},
  );
  const filterValuesOf = (param: string): string[] =>
    chosenFilters[param] ??
    [listFilters?.[param] ?? []].flat().map((v) => String(v));
  // What the person picked, so the box shows its name rather than the value sent (a uuid, a person's id).
  const [picked, setPicked] = useState<EntityOption | null>(null);
  // The same for a many-valued field: the name of each person picked since the dry run last named them.
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});
  const id = fieldId(field.path);
  // A switched-off object has no records to pick; an unknown one is a manifest fault.
  const object = index.isObjectEnabled(field.object)
    ? index.objectOf(field.object)
    : null;

  if (!object?.list) {
    if (index.isObjectKnown(field.object)) {
      console.warn("object_ui.entity_field.object_unavailable", {
        object: field.object,
      });
    } else {
      console.error("object_ui.entity_field.unknown_object", {
        object: field.object,
      });
    }
    return null;
  }
  const list = object.list;
  const valueField = field.hint.entity_value ?? list.key_field;
  const chosen = field.multiple ? chosenOf(value) : [];
  const current =
    !field.multiple && typeof value === "string" && value
      ? picked?.id === value
        ? namedOption(picked)
        : knownAs?.id === value
          ? namedOption(knownAs)
          : { id: value, label: valueLabel ?? value }
      : null;
  const nameOf = (identifier: string) =>
    pickedNames[identifier] ?? valueLabels?.[identifier] ?? identifier;
  const pick = (identifier: string | null, option: EntityOption | null) => {
    if (!field.multiple) return onChange(identifier, option);
    if (identifier && !chosen.includes(identifier)) {
      if (option)
        setPickedNames((names) => ({ ...names, [identifier]: option.label }));
      onChange([...chosen, identifier]);
    }
  };
  const chosenFilterParams = Object.fromEntries(
    choices.flatMap((c) => {
      const values = filterValuesOf(c.param);
      return values.length ? [[c.param, values]] : [];
    }),
  );
  const filters = {
    ...listScope,
    ...(hasSuggestions && isSuggestedOnly ? listFilters : {}),
    ...chosenFilterParams,
  };
  // Once the field holds a value, the filters that found it have done their job.
  const showsFilterChoices =
    choices.length > 0 && (field.multiple || current === null);
  // A scope list with nothing in it matches nobody (the list would read it as no filter and offer everyone).
  const hasNobodyToFind = Object.values(listScope ?? {}).some(
    (v) => Array.isArray(v) && v.length === 0,
  );
  const canCreate = create?.enabled === true;

  const filterChoices = showsFilterChoices ? (
    <div className="space-y-3">
      {choices.map((choice) => {
        const values = filterValuesOf(choice.param);
        const labelOf = (v: string) =>
          choice.options.find((o) => o.value === v)?.label ?? v;
        const rest = choice.options.filter((o) => !values.includes(o.value));
        const set = (next: string[]) =>
          setChosenFilters((all) => ({ ...all, [choice.param]: next }));
        return (
          <div key={choice.param} className="space-y-1.5">
            <div className="text-sm font-medium">{choice.label}</div>
            <div className="flex flex-wrap items-center gap-2">
              {values.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-sm"
                >
                  {labelOf(v)}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Remove ${labelOf(v)}`}
                    disabled={disabled}
                    onClick={() => set(values.filter((x) => x !== v))}
                  >
                    ×
                  </button>
                </span>
              ))}
              {values.length === 0 && (
                <span className="text-sm text-muted-foreground">
                  All {choice.label.toLowerCase()}
                </span>
              )}
              {rest.length > 0 && (
                <select
                  aria-label={`Add to ${choice.label}`}
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm"
                  value=""
                  disabled={disabled}
                  onChange={(e) => {
                    if (e.target.value) set([...values, e.target.value]);
                  }}
                >
                  <option value="">Add…</option>
                  {rest.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <>
      {filterChoices}
      <FieldFrame
        id={id}
        label={field.label}
        required={field.required}
        errors={errors}
        hint={
          hasSuggestions || note ? (
            <div className="space-y-1.5">
              {hasSuggestions && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`${id}-suggested`}
                    checked={isSuggestedOnly}
                    disabled={disabled}
                    onCheckedChange={(checked) =>
                      setSuggestedOnly(checked === true)
                    }
                  />
                  <Label
                    htmlFor={`${id}-suggested`}
                    className="text-sm font-normal text-muted-foreground"
                  >
                    Suggested only
                  </Label>
                </div>
              )}
              {note && (
                <p id={`${id}-note`} className="text-xs text-muted-foreground">
                  {note}
                </p>
              )}
            </div>
          ) : undefined
        }
      >
        {field.multiple && chosen.length > 0 && (
          <ul className="flex flex-wrap gap-1.5" aria-label={field.label}>
            {chosen.map((identifier) => (
              <li
                key={identifier}
                className="flex items-center gap-1 rounded-md border bg-muted px-2 py-0.5 text-sm"
              >
                {nameOf(identifier)}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  disabled={disabled}
                  aria-label={`Remove ${nameOf(identifier)}`}
                  onClick={() => {
                    const rest = chosen.filter((c) => c !== identifier);
                    onChange(rest.length > 0 ? rest : null);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <EntityCombobox
          // A new set of filters is a new list: start the search again.
          key={isSuggestedOnly ? "suggested" : "all"}
          value={current}
          disabled={disabled || hasNobodyToFind}
          ariaLabel={field.label}
          placeholder={`Find ${field.label.toLowerCase()}…`}
          {...invalidProps(id, errors)}
          fetchPage={async (q, page) => {
            const result = await adapter.list(list, {
              ...emptyListQuery(),
              q: q || null,
              filters: { ...filters },
              page,
              page_size: 20,
            });
            // A person at two of the scoped customers is two rows, and one choice.
            const options = result.items.map((item) =>
              optionOf(object, item, valueField),
            );
            return {
              options: options.filter(
                (option, i) =>
                  options.findIndex((o) => o.id === option.id) === i,
              ),
              hasMore: result.page < result.total_pages,
            };
          }}
          onChange={(option) => {
            setPicked(option);
            pick(option?.id ?? null, option);
          }}
          {...(canCreate && create
            ? {
                createLabel: create.label,
                onCreate: () => {
                  void onCreate(create).then((created) => {
                    if (created) {
                      const option = { id: created, label: created };
                      setPicked(option);
                      pick(created, option);
                    }
                  });
                },
              }
            : {})}
        />
      </FieldFrame>
    </>
  );
}
